import { useState, useEffect, useCallback } from 'react';
import { debounce } from 'lodash';
import {
  fetchBreakingNewArticles,
  fetchCommentsByUser,
  fetchListArticle,
  fetchListFollowing,
  fetchListForYou,
  fetchListRecent,
  fetchMyReadingList
} from '../apis/discovery.api';
import {
  fetchListLikedArticlesById,
  fetchListReactedArticlesById,
  fetchListTwigsGivenArticlesById
} from '../apis/article.api';
import { useSearchParams } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import { profileAtom } from '../components/Settings/atom';

export const useGetListArticleByFilter = (params) => {
  const [loading, setLoading] = useState(false);
  const [listArticle, setListArticle] = useState([]);
  const [total, setTotal] = useState(0);
  const [searchParams] = useSearchParams();
  const profile = useAtomValue(profileAtom);
  const [isInitialized, setIsInitialized] = useState(false);
  const [shouldAutoFetchSecondPage, setShouldAutoFetchSecondPage] = useState(true);

  const [pagination, setPagination] = useState({
    page: 1,
    limit: params.limit || 4,
    totalPage: 0
  });

  const handleLoadMore = useCallback(
    debounce(() => {
      if (params.type === 'newsHub' || params.type === 'perspectives') {
        return;
      }

      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      const scrolled = window.scrollY;

      // Trigger loading when user is within 1000px of the bottom (increased from 300px)
      // This gives more time for content to load before user sees white space
      if (scrolled >= scrollable - 1000 && !loading && pagination.page < pagination.totalPage) {
        setLoading(true);
        setPagination((prevPagination) => ({
          ...prevPagination,
          page: prevPagination.page + 1
        }));
      }
    }, 100), // Debounce scroll events by 100ms for better performance
    [params.type, loading, pagination.page, pagination.totalPage]
  );

  useEffect(() => {
    window.addEventListener('scroll', handleLoadMore);
    return () => {
      window.removeEventListener('scroll', handleLoadMore);
      // Cancel any pending debounced calls when component unmounts
      handleLoadMore.cancel();
    };
  }, [handleLoadMore]);

  useEffect(() => {
    setPagination({ page: 1, limit: params.limit || 4, totalPage: 0 });
    setListArticle([]);
    setShouldAutoFetchSecondPage(true); // Reset auto-fetch flag when filters change
    window.scrollTo(0, 0);
  }, [params.type, params.topic]);

  useEffect(() => {
    const fetchArticle = async () => {
      const typesRequireProfileOwnerId = [
        'myLikes',
        'myReactions',
        'myTwigsGiven',
        'myArticles',
        'myComments'
      ];
      const needProfileOwnerId = typesRequireProfileOwnerId.includes(params.type);

      if (needProfileOwnerId && !params.profileOwnerId) {
        return;
      }

      if (!needProfileOwnerId || params.profileOwnerId) {
        setIsInitialized(true);
      }

      setLoading(true);
      try {
        let payload = {
          page: pagination.page,
          limit: pagination.limit,
          ...params,
          topicIds: params.topic,
          userId: profile?.id,
          creatorIds: [params.profileOwnerId],
          isShowIgnoring: params.isShowIgnoring || false
        };

        if (params.type === 'myComments') {
          const { userId, creatorIds, ...cleanPayload } = payload;
          payload = cleanPayload;
        }

        let result = [];
        switch (params.type) {
          case 'myReading':
            result = await fetchMyReadingList(payload);
            break;
          case 'recent':
            result = await fetchListRecent({ ...payload, topicIds: undefined });
            break;
          case 'breaking':
            result = await fetchBreakingNewArticles({ ...payload, topicIds: undefined });
            break;
          case 'forYou':
            result = await fetchListForYou({ ...payload, topicIds: undefined });
            break;
          case 'following':
            result = await fetchListFollowing({ ...payload, topicIds: undefined });
            break;
          case 'categories':
            result = await fetchListArticle({
              ...payload,
              topic: undefined
            });
            break;
          case 'storyId':
            result = await fetchListArticle({
              ...payload,
              storyId: params.storyId,
              topicIds: undefined
            });
            break;
          case 'myLikes':
            result = await fetchListLikedArticlesById(payload.profileOwnerId);
            break;
          case 'myReactions':
            result = await fetchListReactedArticlesById(payload.profileOwnerId);
            break;
          case 'myTwigsGiven':
            result = await fetchListTwigsGivenArticlesById(payload.profileOwnerId);
            break;
          case 'myArticles':
            result = await fetchListArticle(payload);
            break;
          case 'myComments':
            result = await fetchCommentsByUser(payload);
            break;
          case 'newsHub':
          case 'perspectives':
            // These tabs handle their own data fetching
            result = { data: [], totalCount: 0, totalPage: 0 };
            break;
          default:
            result = await fetchListArticle(payload);
            break;
        }

        if (result) {
          setListArticle((prevList) =>
            pagination.page === 1 ? result.data : [...prevList, ...result.data]
          );
          setPagination((prevPagination) => ({
            ...prevPagination,
            totalPage: result.totalPage
          }));
          setTotal(result?.totalCount);

          // Auto-fetch second page after first page loads (only on initial load)
          if (pagination.page === 1 && shouldAutoFetchSecondPage && result.totalPage > 1) {
            setShouldAutoFetchSecondPage(false); // Prevent multiple auto-fetches
            // Use setTimeout to ensure the first page render completes before fetching second page
            setTimeout(() => {
              setPagination((prevPagination) => ({
                ...prevPagination,
                page: 2
              }));
            }, 100);
          }
        }
      } catch (error) {
        console.error('Error fetching articles:', error);
      } finally {
        setLoading(false);
      }
    };

    if (pagination.page > 0) {
      fetchArticle();
    }
  }, [
    params.type,
    pagination.page,
    params.topic,
    params.profileOwnerId,
    shouldAutoFetchSecondPage
  ]);

  useEffect(() => {
    if (searchParams.get('topicId')) {
      setPagination((prevPagination) => ({
        ...prevPagination,
        page: 1
      }));
    }
  }, [searchParams]);

  return {
    loading: loading || !isInitialized,
    listArticle,
    handleLoadMore,
    setListArticle,
    total,
    setPagination
  };
};
